import { Container, FormContext, Layout } from '@/components/SettingsLayout';
import CompanySkeleton from '@/pages/companies/components/CompanySkeleton';
import { Plan } from '@/services/Plans/plans.service';
import { useGetAllExtensionsQuery, useGetAllFeatureGroupsWithFeaturesQuery } from '@/store';
import {
  useCreatePlanMutation,
  useGetPlanByIdQuery,
  useUpdatePlanMutation,
} from '@/store/slices/plansSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetUser } from '@/hooks';
import {
  createPlanData,
  getDefaultDataFromPlan,
  getDefaultPlanData,
  transformFormDataToFeatures,
} from '../../helpers/utils';
import { PlanLeftPart } from './PlanLeftPart';
import PlanRestrictions from './PlanRestrictions';
import PlanPricing from './PlanPricing';

type Props = {
  baseUrl: string;
  plan?: Plan;
};

const SinglePlanPage = ({ baseUrl }: Props) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  const { t } = useTranslation();
  const { user, company } = useGetUser();
  const { data: singlePlan, isLoading } = useGetPlanByIdQuery(
    planId && user?.targetOrganizationId
      ? { id: planId, organizationId: user.targetOrganizationId }
      : skipToken,
  );
  const isDetailsPage = searchParams.get('tab') === 'details';
  const isPricingPage = searchParams.get('tab') === 'pricing';
  const navigate = useNavigate();
  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();
  const { data: allFeatureGroupsWithFeatures, isLoading: featuresLoader } =
    useGetAllFeatureGroupsWithFeaturesQuery(user?.targetOrganizationId || '');
  const { data: allExtensions, isLoading: extenstionsLoader } = useGetAllExtensionsQuery({
    organizationId: user?.targetOrganizationId || '',
  });

  const [defaultValues, setDefaultValues] = useState<Record<string, any> | null>(null);
  // Track which plan we already seeded the form for. Without this the form
  // resets on every RTK Query background refetch (FormContext re-runs reset
  // when defaultValues identity changes) and wipes whatever the user typed.
  const seededForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!allFeatureGroupsWithFeatures) return;
    if (!allExtensions) return;
    if (planId && !singlePlan) return;
    const key = planId ?? '__new__';
    if (seededForRef.current === key) return;
    seededForRef.current = key;

    const newDefaultData =
      planId && singlePlan
        ? getDefaultDataFromPlan(singlePlan)
        : getDefaultPlanData(allFeatureGroupsWithFeatures);

    setDefaultValues(newDefaultData);
  }, [allFeatureGroupsWithFeatures, allExtensions, singlePlan, planId]);

  const submitForm = (data: Record<string, any>) => {
    if (!id || !allExtensions || !allFeatureGroupsWithFeatures) return;
    const featureGroupNames = allFeatureGroupsWithFeatures.map((group) => group.name);
    if (planId && singlePlan) {
      const features = transformFormDataToFeatures(data, featureGroupNames);
      const planData = createPlanData(data, id, features);

      updatePlan({
        id: planId,
        planData: planData,
        organizationId: user?.targetOrganizationId || '',
      })
        .unwrap()
        .then(() => {
          toast.success(t('Plans.update_successful'));
          navigate(`${baseUrl}`);
        })
        .catch(() => {
          toast.error(t('Plans.update_error'));
        });
      return;
    }

    const features = transformFormDataToFeatures(data, featureGroupNames);
    if (!company) {
      return;
    }
    const planData = createPlanData(data, company._id, features);

    createPlan({ planData, organizationId: user?.targetOrganizationId || '' })
      .unwrap()
      .then(() => {
        toast.success(t('Plans.create_success'));
        navigate(`${baseUrl}`);
      })
      .catch(() => {
        toast.error(t('Plans.create_error'));
      });
  };

  if (isLoading || extenstionsLoader || featuresLoader || !defaultValues) {
    return (
      <Layout fullHeight>
        <CompanySkeleton />
      </Layout>
    );
  }

  return (
    <Layout fullHeight>
      <PlanLeftPart baseUrl={baseUrl} plan={singlePlan} />
      <Container size='lg'>
        {allFeatureGroupsWithFeatures && allExtensions && isDetailsPage && (
          <FormContext
            submitHandler={submitForm}
            overflow='auto'
            defaultValues={defaultValues}
            resetContext={true}
          >
            <PlanRestrictions
              plan={singlePlan}
              featureGroup={allFeatureGroupsWithFeatures}
              extensions={allExtensions}
              isSaving={isCreating || isUpdating}
            />
          </FormContext>
        )}
        {isPricingPage && planId && singlePlan && <PlanPricing />}
      </Container>
    </Layout>
  );
};

export default SinglePlanPage;
